import axios, { type InternalAxiosRequestConfig } from "axios";
import { expireSession, sessionSnapshot, isCurrentSession, InvalidSessionError } from "@/lib/session-expiration";
import { isPublicRequest, requestScope } from "./session";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim();
if (!apiBaseUrl) {
  throw new Error("VITE_API_BASE_URL is not configured. Set it in the frontend Render environment.");
}

export const api = axios.create({
  baseURL: apiBaseUrl,
  headers: { "Content-Type": "application/json" },
});

const sessions = new WeakMap<InternalAxiosRequestConfig, ReturnType<typeof sessionSnapshot>>();

api.interceptors.request.use((config) => {
  if (isPublicRequest(config.url ?? "", config.method)) {
    delete config.headers.Authorization;
    return config;
  }
  const snapshot = sessionSnapshot(requestScope(config.url ?? ""));
  const token = snapshot.token;
  sessions.set(config, snapshot);
  if (!token) throw new InvalidSessionError();
  config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => {
    const snapshot = sessions.get(response.config);
    if (snapshot && !isCurrentSession(requestScope(response.config.url ?? ""), snapshot)) {
      throw new InvalidSessionError();
    }
    return response;
  },
  (error) => {
    const config = error.config;
    const snapshot = config && sessions.get(config);
    if (snapshot && !isCurrentSession(requestScope(config.url ?? ""), snapshot)) {
      return Promise.reject(new InvalidSessionError());
    }
    if (error.response?.status === 401 && config && !isPublicRequest(config.url ?? "", config.method)) {
      const authorization = config.headers?.Authorization;
      if (typeof authorization === "string" && authorization.startsWith("Bearer ")) {
        expireSession(requestScope(config.url ?? ""), authorization.slice(7));
      }
    }
    return Promise.reject(error);
  },
);
